import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import {
  DATO_DE_IVA_CONTENIDO,
  DATO_DE_OTROS_IMPUESTOS,
  LEYENDA_DEL_EMISOR,
  LEYENDA_DEL_RECEPTOR,
  LEYENDA_PARA_MONOTRIBUTISTA,
  TIPOS_DE_COMPROBANTE,
  TITULO_DE_TRANSPARENCIA_FISCAL,
  discriminaIva,
  llevaLeyendaDeMonotributo,
  llevaTransparenciaFiscal,
  numeroDeComprobante,
  paqueteDePais,
  type ClaseDeComprobante,
  type CondicionDelEmisor,
  type CondicionDelReceptor,
} from '@actualgold/localizacion';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Cargando, Problema } from '../componentes/ui/estados';
import { conDosDecimales } from '../venta/PiezasDeVenta';

interface LineaDelTicket {
  readonly orden: number;
  readonly descripcion: string;
  readonly codigo: string;
  readonly unidad: string;
  readonly cantidad: string;
  readonly precioUnitario: string;
  readonly descuentos: string | null;
  readonly importe: string;
  readonly neto: string;
  readonly iva: string;
  readonly alicuotaDeIva: string;
}

interface ImpuestoDelTicket {
  readonly codigo: string;
  readonly alicuota: string;
  readonly neto: string;
  readonly iva: string;
}

interface TicketPublico {
  readonly numero: string;
  readonly emitidoEn: string;
  readonly estado: string;
  readonly subtotal: string;
  readonly descuentoTotal: string;
  readonly redondeo: string;
  readonly total: string;
  readonly netoTotal: string;
  readonly ivaTotal: string;
  readonly clase: ClaseDeComprobante | null;
  /** Códigos del país del comercio. Las leyendas de la RG 1415 sólo van con letra (E35). */
  readonly condicionDelEmisor: string | null;
  readonly condicionDelReceptor: string | null;
  readonly empresa: string | null;
  readonly sucursal: string | null;
  readonly razonSocialDelComercio: string | null;
  readonly identificacionDelComercio: string | null;
  /** Vacíos en los comprobantes anteriores a E35, que son argentinos. */
  readonly paisDelComercio: string | null;
  readonly tipoDeIdentificacionDelComercio: string | null;
  readonly tipoDeIdentificacionDelCliente: string | null;
  readonly domicilioDelComercio: string | null;
  readonly numeroDelPuntoDeVenta: number | null;
  readonly cliente: string | null;
  readonly identificacionDelCliente: string | null;
  readonly domicilioDelCliente: string | null;
  readonly lineas: readonly LineaDelTicket[];
  readonly impuestos: readonly ImpuestoDelTicket[];
  readonly estadoFiscal: string;
  readonly cae: string | null;
  readonly caeVencimiento: string | null;
  readonly tipo: string;
  /** El comprobante que esta nota de crédito ajusta, ya formateado por el servidor. */
  readonly ajusta: string | null;
  /** El texto del QR de la RG 4892, armado por el servidor. Nulo mientras no haya CAE. */
  readonly qrDelOrganismo: string | null;
}

/** `yyyymmdd` como lo escribe la gente. Es como el organismo devuelve el vencimiento del CAE. */
function fechaDelOrganismo(compacta: string): string {
  if (!/^\d{8}$/.test(compacta)) return compacta;
  return `${compacta.slice(6, 8)}/${compacta.slice(4, 6)}/${compacta.slice(0, 4)}`;
}

/** La cantidad se muestra sin los tres decimales de la escala cuando no hacen falta. */
function cantidadVisible(cantidad: string): string {
  return cantidad.replace(/\.0+$/, '');
}

/** La alícuota sin ceros de relleno: 21 y no 21,0000. */
function alicuotaVisible(alicuota: string): string {
  return alicuota.replace(/\.?0+$/, '');
}

/**
 * La segunda línea de cada renglón: "3 UN × 1.000,00 − 30,10%".
 *
 * Se arma como cadena y no como composición de fragmentos porque es una sola frase, y partida
 * en pedazos dentro del JSX se vuelve imposible de leer y de traducir.
 */
function detalleDeLinea(linea: LineaDelTicket): string {
  const base = `${cantidadVisible(linea.cantidad)} ${linea.unidad} × ${conDosDecimales(linea.precioUnitario)}`;
  return linea.descuentos === null ? base : `${base} − ${linea.descuentos}%`;
}

/**
 * El comprobante impreso: en rollo de 80 mm o en hoja A4, y consultable con su QR.
 *
 * Una sola pantalla para las tres cosas. Lo que se imprime en la térmica, lo que sale en la hoja
 * y lo que ve quien escanea el QR tienen que decir exactamente lo mismo; si fueran vistas
 * distintas, tarde o temprano una quedaría con un dato que la otra no tiene, y el papel es el que
 * el cliente guarda.
 *
 * **Todo lo que muestra sale de columnas congeladas del comprobante.** Se lee sin sesión, con el
 * token del QR, y la política de la base sólo alcanza a ese comprobante y sus líneas: no hay
 * ninguna consulta a `empresas` ni a `terceros` detrás de este papel, ni la puede haber.
 *
 * Lo que cambia según la clase está en la norma y no en el gusto de nadie: el A discrimina el
 * impuesto renglón por renglón, el B lleva el precio final y el bloque de transparencia fiscal, y
 * el C no lleva ninguno de los dos porque quien lo emite no liquida IVA. Las tres reglas viven en
 * `@actualgold/localizacion`, con los artículos citados.
 */
export function Ticket(): React.ReactElement {
  const { t } = useTranslation();
  const token = globalThis.location.pathname.split('/').pop() ?? '';
  const [qr, setQr] = useState<string | null>(null);
  // El rollo es lo que sale por la térmica del mostrador y es lo que se ve por omisión. La hoja
  // existe para el cliente que pide "la factura en papel A4", que en el mayorista es lo normal.
  const [enHoja, setEnHoja] = useState(false);

  const consulta = useQuery({
    queryKey: ['ticket', token],
    queryFn: () => pedir<TicketPublico>(`/publico/comprobantes/${token}`),
  });

  const direccion = `${globalThis.location.origin}/t/${token}`;
  // **Un solo QR en el papel, y el que corresponde.** Con CAE va el de la RG 4892, que es el que
  // el organismo exige y el que un inspector escanea; sin CAE, el del ticket consultable, que es
  // el único que existe para un documento que todavía no está autorizado. Dos códigos juntos
  // serían dos cosas para escanear y ninguna clara.
  const contenidoDelQr = consulta.data?.qrDelOrganismo ?? direccion;

  useEffect(() => {
    // El QR se dibuja en el navegador y no llega del servidor: así el ticket se imprime igual
    // aunque la impresora esté en otra máquina y no haya red hacia afuera.
    void QRCode.toDataURL(contenidoDelQr, { margin: 0, width: 160 })
      .then(setQr)
      .catch(() => {
        setQr(null);
      });
  }, [contenidoDelQr]);

  if (consulta.isPending) return <Cargando />;
  if (consulta.isError) return <Problema error={consulta.error} />;

  const ticket = consulta.data;
  const clase = ticket.clase;
  // Las condiciones se leen con la tabla argentina sólo cuando hay letra: un comprobante sin
  // clase es interno —o de un país sin letras— y no lleva las leyendas de la RG 1415.
  const emisor = clase === null ? null : (ticket.condicionDelEmisor as CondicionDelEmisor | null);
  const receptor =
    clase === null ? null : (ticket.condicionDelReceptor as CondicionDelReceptor | null);
  const conDiscriminacion = clase !== null && discriminaIva(clase);
  // El país lo congeló el comprobante al emitirse: el QR no puede leer la empresa (E35). Sin dato,
  // es un comprobante anterior, y entonces es argentino.
  const paquete = paqueteDePais(ticket.paisDelComercio ?? 'AR');
  // En un país sin letras no hay A ni B que decidan cómo se informa el impuesto: se dice cuánto IVA
  // de cada tasa va incluido en el precio, que es lo que el cliente necesita leer.
  const conIvaIncluido =
    clase === null &&
    paquete !== undefined &&
    !paquete.tieneFiscoElectronico &&
    ticket.impuestos.some((parte) => Number(parte.iva) !== 0);
  /** «RUT 21-100342-001-7» con el formato del país; los anteriores a E35 siguen diciendo CUIT. */
  const identificacion = (tipo: string | null, numero: string): string =>
    tipo === null
      ? t('ticket.cuit', { cuit: numero })
      : t('ticket.identificacion', {
          tipo: t(`identificaciones.${tipo}`, tipo),
          numero: paquete?.formatearIdentificacion(tipo, numero) ?? numero,
        });
  const conTransparencia =
    clase !== null && emisor !== null && llevaTransparenciaFiscal(emisor, clase);

  const numero =
    ticket.numeroDelPuntoDeVenta === null
      ? t('ticket.numero', { numero: ticket.numero })
      : numeroDeComprobante(ticket.numeroDelPuntoDeVenta, ticket.numero);

  return (
    <div
      className={
        enHoja
          ? 'mx-auto max-w-[190mm] p-4 print:max-w-none print:p-0'
          : 'mx-auto max-w-[80mm] p-4 print:max-w-none print:p-0'
      }
    >
      {/* La barra de acciones no se imprime: en el papel sólo va el comprobante. */}
      <div className="mb-4 flex justify-end gap-2 print:hidden">
        <Boton
          onClick={() => {
            setEnHoja((previo) => !previo);
          }}
        >
          {enHoja ? t('ticket.verEnRollo') : t('ticket.verEnHoja')}
        </Boton>
        <Boton
          tono="principal"
          onClick={() => {
            globalThis.print();
          }}
        >
          {t('ticket.imprimir')}
        </Boton>
      </div>

      <article
        className={
          enHoja
            ? 'hoja border border-borde p-6 text-[12px] leading-snug'
            : 'ticket font-mono text-[11px] leading-tight'
        }
      >
        <header className={enHoja ? 'grid grid-cols-[1fr_auto_1fr] items-start gap-2' : ''}>
          <div className={enHoja ? '' : 'text-center'}>
            {/* La razón social es el nombre legal y va primero en un comprobante fiscal; el de
                fantasía queda debajo, que es como lo conoce el cliente. */}
            <p className="text-sm font-semibold">
              {ticket.razonSocialDelComercio ?? ticket.empresa ?? ''}
            </p>
            {ticket.razonSocialDelComercio === null ? null : (
              <p className="text-[11px]">{ticket.empresa ?? ''}</p>
            )}
            <p>{ticket.sucursal ?? ''}</p>
            {ticket.identificacionDelComercio === null ? null : (
              <p>
                {identificacion(
                  ticket.tipoDeIdentificacionDelComercio,
                  ticket.identificacionDelComercio,
                )}
              </p>
            )}
            {ticket.domicilioDelComercio === null ? null : (
              <p>{t('ticket.domicilio', { domicilio: ticket.domicilioDelComercio })}</p>
            )}
            {/* La leyenda de condición del emisor: Anexo II, Título I, inciso a), punto 5. */}
            {emisor === null ? null : <p className="font-semibold">{LEYENDA_DEL_EMISOR[emisor]}</p>}
          </div>

          {/* La letra en grande, con el código de tipo debajo. En la hoja va en el recuadro del
              medio, que es donde la busca cualquiera que haya visto una factura argentina. */}
          {clase === null ? null : (
            <div
              className={
                enHoja
                  ? 'border border-borde px-4 py-1 text-center'
                  : 'mt-2 border border-borde py-1 text-center'
              }
            >
              <p className="text-2xl font-bold leading-none">{clase}</p>
              <p className="text-[10px]">
                {t('ticket.codigoDeTipo', { codigo: TIPOS_DE_COMPROBANTE[clase].factura })}
              </p>
            </div>
          )}

          <div className={enHoja ? 'text-right' : 'mt-1 text-center'}>
            {/* Una nota de crédito lo dice arriba de todo. Quien la recibe tiene que saber en el
                primer vistazo que no es una factura: son documentos que se archivan distinto. */}
            {ticket.tipo === 'nota_de_credito' ? (
              <p className="font-semibold">{t('ticket.notaDeCredito')}</p>
            ) : null}
            <p className="font-semibold">{numero}</p>
            <p>{new Date(ticket.emitidoEn).toLocaleString()}</p>
            <p className="text-[10px]">{t('ticket.original')}</p>
            {ticket.ajusta === null ? null : (
              <p className="text-[10px]">{t('ticket.ajusta', { comprobante: ticket.ajusta })}</p>
            )}
          </div>
        </header>

        <hr className="my-2 border-dashed border-borde" />

        {/* El receptor. Sin cliente identificado va sólo la leyenda "A CONSUMIDOR FINAL", que es
            lo que la norma pide y todo lo que hay para decir. */}
        <section className={enHoja ? 'text-[11px]' : 'text-center'}>
          {ticket.cliente === null ? null : (
            <p>{t('ticket.cliente', { nombre: ticket.cliente })}</p>
          )}
          {ticket.identificacionDelCliente === null ? null : (
            <p>
              {identificacion(
                ticket.tipoDeIdentificacionDelCliente,
                ticket.identificacionDelCliente,
              )}
            </p>
          )}
          {ticket.domicilioDelCliente === null ? null : (
            <p>{t('ticket.domicilio', { domicilio: ticket.domicilioDelCliente })}</p>
          )}
          {receptor === null ? null : <p>{LEYENDA_DEL_RECEPTOR[receptor]}</p>}
        </section>

        <hr className="my-2 border-dashed border-borde" />

        <table className="w-full">
          <tbody>
            {ticket.lineas.map((linea) => (
              <tr key={linea.orden} className="align-top">
                <td colSpan={2} className="pb-1">
                  <span className="block">{linea.descripcion}</span>
                  <span className="block text-[10px]">{detalleDeLinea(linea)}</span>
                </td>
                {/* En el A el renglón muestra el neto: el impuesto se suma aparte, más abajo.
                    En el B y el C, el importe es el final que el cliente paga. */}
                <td className="pb-1 text-right align-bottom">
                  {conDosDecimales(conDiscriminacion ? linea.neto : linea.importe)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <hr className="my-2 border-dashed border-borde" />

        <dl className="flex flex-col gap-0.5">
          {conDiscriminacion ? (
            <>
              {/* Anexo II, Título IV, inciso a), punto 1: la alícuota y el monto del impuesto,
                  "a continuación de la descripción o detalle de la operación". */}
              <div className="flex justify-between">
                <dt>{t('ticket.neto')}</dt>
                <dd>{conDosDecimales(ticket.netoTotal)}</dd>
              </div>
              {ticket.impuestos.map((parte) => (
                <div key={parte.codigo} className="flex justify-between">
                  <dt>
                    {t('ticket.ivaPorAlicuota', { alicuota: alicuotaVisible(parte.alicuota) })}
                  </dt>
                  <dd>{conDosDecimales(parte.iva)}</dd>
                </div>
              ))}
            </>
          ) : (
            <div className="flex justify-between">
              <dt>{t('ticket.subtotal')}</dt>
              <dd>{conDosDecimales(ticket.subtotal)}</dd>
            </div>
          )}
          {conIvaIncluido
            ? ticket.impuestos.map((parte) => (
                <div key={parte.codigo} className="flex justify-between text-[10px]">
                  <dt>{t('ticket.ivaIncluido', { alicuota: alicuotaVisible(parte.alicuota) })}</dt>
                  <dd>{conDosDecimales(parte.iva)}</dd>
                </div>
              ))
            : null}

          {Number(ticket.descuentoTotal) === 0 ? null : (
            <div className="flex justify-between">
              <dt>{t('ticket.descuento')}</dt>
              <dd>{conDosDecimales(ticket.descuentoTotal)}</dd>
            </div>
          )}
          {Number(ticket.redondeo) === 0 ? null : (
            <div className="flex justify-between">
              <dt>{t('ticket.redondeo')}</dt>
              <dd>{conDosDecimales(ticket.redondeo)}</dd>
            </div>
          )}
          <div className="mt-1 flex justify-between text-base font-semibold">
            <dt>{t('ticket.total')}</dt>
            <dd>{conDosDecimales(ticket.total)}</dd>
          </div>
        </dl>

        {/* La leyenda del artículo 15 para el A a un monotributista. Texto literal de la norma. */}
        {clase !== null && receptor !== null && llevaLeyendaDeMonotributo(clase, receptor) ? (
          <p className="mt-2 text-[9px] leading-tight">{LEYENDA_PARA_MONOTRIBUTISTA}</p>
        ) : null}

        {/* El bloque de transparencia fiscal de la RG 5614: "en el espacio inferior izquierdo",
            el título y debajo los dos datos, cada uno con su valor. */}
        {conTransparencia ? (
          <section className="mt-3 text-left text-[10px]">
            <p className="font-semibold">{TITULO_DE_TRANSPARENCIA_FISCAL}</p>
            <div className="flex justify-between">
              <span>{DATO_DE_IVA_CONTENIDO}</span>
              <span>{conDosDecimales(ticket.ivaTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>{DATO_DE_OTROS_IMPUESTOS}</span>
              {/* Cero, y dicho como cero: todavía no hay percepciones ni impuestos internos de
                  dónde sacarlo. Omitir el renglón sería incumplir; poner un número inventado,
                  peor. */}
              <span>{conDosDecimales('0')}</span>
            </div>
          </section>
        ) : null}

        {/* El ticket interno de antes de la facturación electrónica lo sigue diciendo. */}
        {clase === null ? <p className="mt-2 font-semibold">{t('ticket.noFiscal')}</p> : null}

        <footer className="mt-3 flex flex-col items-center gap-1">
          {/* El CAE va junto al código, que es donde lo busca quien controla. */}
          {ticket.cae === null ? null : (
            <p className="font-semibold">{t('ticket.cae', { cae: ticket.cae })}</p>
          )}
          {ticket.caeVencimiento === null ? null : (
            <p>{t('ticket.caeVencimiento', { fecha: fechaDelOrganismo(ticket.caeVencimiento) })}</p>
          )}
          {/* Un comprobante encolado lo dice. No es un error —el mostrador no espera a ARCA— pero
              el papel no puede callarlo: quien lo recibe tiene derecho a saber que todavía no está
              autorizado. */}
          {ticket.estadoFiscal === 'pendiente' ? (
            <p className="text-center text-[10px]">{t('ticket.sinCae')}</p>
          ) : null}

          {qr === null ? null : (
            <img
              src={qr}
              alt={ticket.qrDelOrganismo === null ? t('ticket.qr') : t('ticket.qrDelOrganismo')}
              width={120}
              height={120}
            />
          )}
          {ticket.qrDelOrganismo === null ? (
            <p className="text-center text-[10px]">{t('ticket.consultaConQr')}</p>
          ) : null}
        </footer>
      </article>
    </div>
  );
}
